import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { AlertBanner } from '@/components/campus';
import { Loader2, Mail } from 'lucide-react';

export const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const { resetPassword } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      await resetPassword(email);
      setSuccess(true);
    } catch (err) {
      setError(err.message || 'Failed to send reset email. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  if (success) {
    return (
      <div data-testid="reset-success" className="text-center">
        <div className="w-16 h-16 rounded-full bg-electric-ice-blue/20 flex items-center justify-center mx-auto mb-6">
          <Mail className="w-8 h-8 text-electric-ice-blue" />
        </div>
        <h1 className="text-2xl font-semibold text-cloud-white tracking-[-0.02em] mb-2">
          Check your email
        </h1>
        <p className="text-soft-silver text-sm mb-6">
          We've sent password reset instructions to <span className="text-cloud-white">{email}</span>
        </p>
        <p className="text-muted-text text-xs mb-6">
          Click the link in your email to reset your password.
        </p>
        <Link 
          to="/login" 
          className="inline-block text-electric-ice-blue hover:underline text-sm"
        >
          Back to sign in
        </Link>
      </div>
    );
  }

  return (
    <div data-testid="forgot-password-page">
      <div className="text-center mb-8">
        <h1 className="text-2xl font-semibold text-cloud-white tracking-[-0.02em]">
          Reset password
        </h1>
        <p className="text-soft-silver text-sm mt-2">
          Enter your email to receive reset instructions
        </p>
      </div>

      {error && (
        <div className="mb-4">
          <AlertBanner 
            variant="error" 
            message={error} 
            dismissible 
            onDismiss={() => setError('')}
          />
        </div>
      )}
      
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="email" className="text-soft-silver">Email</Label>
          <Input
            id="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@university.edu"
            className="bg-obsidian border-white/[0.08] text-cloud-white placeholder:text-muted-text focus:border-electric-ice-blue"
            data-testid="forgot-email"
            required
            disabled={isLoading}
          />
        </div>
        
        <Button 
          type="submit" 
          className="w-full bg-electric-ice-blue text-white hover:bg-electric-ice-blue/90 font-medium"
          data-testid="forgot-submit"
          disabled={isLoading}
        >
          {isLoading ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Sending...
            </>
          ) : (
            'Send reset link'
          )}
        </Button>
      </form>
      
      <p className="text-center text-sm text-soft-silver mt-6">
        Remember your password?{' '}
        <Link 
          to="/login" 
          className="text-electric-ice-blue hover:underline"
          data-testid="back-to-login"
        >
          Sign in
        </Link>
      </p>
    </div>
  );
};

export default ForgotPassword;
